import { h, replace } from '../utils/dom.ts';
import { t, type MessageKey } from '../i18n/index.ts';

/** Shared, unstyled-logic UI building blocks. Styling lives in styles/main.css. */

export function skeleton(lines = 3, kind: 'card' | 'chip' = 'card'): HTMLElement {
  return h('div', { class: `skeleton-group skeleton-${kind}`, 'aria-busy': 'true', 'aria-live': 'polite' },
    Array.from({ length: lines }, () => h('div', { class: 'skeleton' })),
  );
}

export function spinner(): HTMLElement {
  return h('span', { class: 'spinner', role: 'status', 'aria-label': 'Loading' });
}

export function section(title: string, ...children: (Node | null)[]): HTMLElement {
  return h('section', { class: 'section' }, h('h2', { class: 'section-title' }, title), ...children);
}

export function errorBox(key: MessageKey, retry?: () => void): HTMLElement {
  return h('div', { class: 'notice notice-error', role: 'alert' },
    h('p', null, t(key)),
    retry && h('button', { class: 'btn btn-secondary', type: 'button', onClick: retry }, '↻'),
  );
}

export function emptyState(text: string): HTMLElement {
  return h('p', { class: 'empty' }, text);
}

export function statusBadge(status: string, label: string): HTMLElement {
  return h('span', { class: `badge badge-${status}` }, label);
}

// --- Toasts ------------------------------------------------------------------

let toastRoot: HTMLElement | null = null;

export function toast(message: string, kind: 'info' | 'success' | 'error' = 'info', ms = 4000): void {
  if (!toastRoot) {
    toastRoot = h('div', { class: 'toasts', 'aria-live': 'polite' });
    document.body.append(toastRoot);
  }
  const el = h('div', { class: `toast toast-${kind}`, role: kind === 'error' ? 'alert' : 'status' }, message);
  toastRoot.append(el);
  setTimeout(() => {
    el.classList.add('toast-out');
    setTimeout(() => el.remove(), 300);
  }, ms);
}

/** Runs an async load into `target`, showing a skeleton meanwhile and a retryable error on failure. */
export async function loadInto<T>(
  target: HTMLElement,
  load: () => Promise<T>,
  render: (data: T) => Node,
  placeholder: Node = skeleton(),
  errorKey: (e: unknown) => MessageKey = () => 'error.generic',
): Promise<void> {
  replace(target, placeholder);
  try {
    const data = await load();
    replace(target, render(data));
  } catch (e) {
    console.error(e);
    replace(target, errorBox(errorKey(e), () => void loadInto(target, load, render, placeholder, errorKey)));
  }
}
